import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";

// Bespoke landing page for AgentPager (Android). Served at /apps/agentpager.
// Concept: the concrete moment the product exists for — "your agent stopped and
// is waiting for a yes". Real 540x960 store screenshots in phone mockups, a
// static two-command terminal motif (no animation loops), a script-paging
// section, an honest Free/Pro table with no prices, a visible FAQ that is
// mirrored 1:1 into FAQPage JSON-LD, and full meta/OG/Twitter/SoftwareApplication
// SEO. No ratings and no Review/AggregateRating schema — we have no honest
// numbers for this app yet.
//
// Brand: violet #6D28D9 / deep plum #4C1D95 on a light lavender-white page.
//
// ── WCAG AA contrast (checked, all ≥ 4.5:1 for body, ≥ 3:1 for large) ──
//   INK   #1B1630 on BG    #FAF8FF → 16.56:1
//   INK   #1B1630 on CARD  #FFFFFF → 17.44:1
//   INK   #1B1630 on TINT  #F3EEFF → 15.35:1
//   INK   #1B1630 on FAINT #F7F4FF → 16.06:1
//   BODY  #3F3A55 on #FFFFFF → 10.78:1 · on #FAF8FF → 10.24:1
//   SUB   #514B6B on #FAF8FF →  7.76:1 · on #FFFFFF → 8.18:1 · on #F3EEFF → 7.20:1
//   MUTED #6B6486 on #FFFFFF →  5.54:1 · on #FAF8FF → 5.26:1
//   VIOLET #6D28D9 on #FFFFFF → 7.10:1 · on #F3EEFF → 6.25:1
//   PLUM   #4C1D95 on #FFFFFF → 10.95:1 · on #F3EEFF → 9.64:1
//   #FFFFFF on VIOLET #6D28D9 → 7.10:1 · on PLUM #4C1D95 → 10.95:1
//   #FFFFFF on DEEP #2A1358 → 15.76:1 · #DCD2F5 on #2A1358 → 10.94:1
//   GREEN #166534 on #ECFDF5 → 6.77:1 · RED #B42318 on #FEF3F2 → 6.05:1

const PLAY = "https://play.google.com/store/apps/details?id=com.cosmovex.mdpilot";
const SITE = "https://cosmovexapps.com";
const PAGE = SITE + "/apps/agentpager";
const GET = "/apps/agentpager/get";
const SHOT = "/images/agentpager"; // 01..07.webp, 540x960 real store screenshots
const BANNER = "/images/agentpager/feature.webp"; // 1024x500
const LOGO = "/images/play/agentpager.png";

const VIOLET = "#6D28D9";
const PLUM = "#4C1D95";
const DEEP = "#2A1358";
const INK = "#1B1630";
const BODY = "#3F3A55";
const SUB = "#514B6B";
const MUTED = "#6B6486";
const BG = "#FAF8FF";
const CARD = "#FFFFFF";
const TINT = "#F3EEFF";
const FAINT = "#F7F4FF";
const LINE = "#E6DEF7";

const SANS = `-apple-system, "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
const MONO = `"SF Mono", "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace`;

const PRIVACY_LINE = "Your code never leaves your computer · end-to-end encrypted · no account";

const playClick = (e: React.MouseEvent) => {
  e.preventDefault();
  const w = window as any;
  if (w.gtag_report_conversion) w.gtag_report_conversion(PLAY);
  else window.open(PLAY, "_blank", "noopener");
};

const PlayButton: React.FC<{ small?: boolean }> = ({ small }) => (
  <a
    href={PLAY}
    onClick={playClick}
    aria-label="Get AgentPager on Google Play"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.6rem",
      padding: small ? "0.72rem 1.3rem" : "1rem 1.85rem",
      background: `linear-gradient(180deg, ${VIOLET} 0%, ${PLUM} 100%)`,
      color: "#FFFFFF",
      borderRadius: "0.9rem",
      fontWeight: 700,
      fontSize: small ? "0.98rem" : "1.12rem",
      textDecoration: "none",
      border: "1px solid rgba(0,0,0,0.08)",
      boxShadow: "0 1px 2px rgba(42,19,88,0.2), 0 10px 24px -8px rgba(76,29,149,0.45)",
      fontFamily: SANS,
      letterSpacing: "-0.2px",
    }}
  >
    <svg width={small ? 18 : 22} height={small ? 18 : 22} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
    </svg>
    Get it on Google Play
  </a>
);

const GetButton: React.FC<{ onDark?: boolean }> = ({ onDark }) => (
  <a
    href={GET}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.5rem",
      padding: "1rem 1.7rem",
      background: onDark ? "rgba(255,255,255,0.1)" : CARD,
      color: onDark ? "#FFFFFF" : PLUM,
      borderRadius: "0.9rem",
      fontWeight: 700,
      fontSize: "1.12rem",
      textDecoration: "none",
      border: `1.5px solid ${onDark ? "rgba(255,255,255,0.45)" : LINE}`,
      fontFamily: SANS,
      letterSpacing: "-0.2px",
    }}
  >
    Get the app
    <span aria-hidden="true">→</span>
  </a>
);

const PrivacyNote: React.FC<{ center?: boolean; onDark?: boolean }> = ({ center, onDark }) => (
  <p
    style={{
      margin: "0.95rem 0 0",
      fontSize: "0.95rem",
      lineHeight: 1.5,
      color: onDark ? "#DCD2F5" : MUTED,
      textAlign: center ? "center" : "left",
      maxWidth: 470,
      marginLeft: center ? "auto" : undefined,
      marginRight: center ? "auto" : undefined,
    }}
  >
    <span aria-hidden="true" style={{ marginRight: "0.35rem" }}>
      🔒
    </span>
    {PRIVACY_LINE}
  </p>
);

const Phone: React.FC<{ src: string; alt: string; w?: number; eager?: boolean }> = ({
  src,
  alt,
  w = 236,
  eager,
}) => (
  <div
    style={{
      width: w,
      maxWidth: "100%",
      borderRadius: Math.round(w * 0.13),
      background: "#150E26",
      padding: Math.max(4, Math.round(w * 0.022)),
      boxShadow: "0 30px 60px -20px rgba(42,19,88,0.34), 0 10px 24px -14px rgba(42,19,88,0.2)",
    }}
  >
    <div style={{ borderRadius: Math.round(w * 0.11), overflow: "hidden", background: CARD, lineHeight: 0 }}>
      <img
        src={src}
        alt={alt}
        width={540}
        height={960}
        loading={eager ? "eager" : "lazy"}
        decoding={eager ? "sync" : "async"}
        style={{ width: "100%", height: "auto", display: "block" }}
      />
    </div>
  </div>
);

/* ── Static terminal motif: the whole setup, two commands ── */
const Terminal: React.FC = () => (
  <div
    style={{
      background: CARD,
      border: `1px solid ${LINE}`,
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: "0 24px 50px -26px rgba(42,19,88,0.3)",
      fontFamily: MONO,
      width: "100%",
      maxWidth: 520,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "0.65rem 1rem",
        borderBottom: `1px solid ${LINE}`,
        background: FAINT,
      }}
    >
      <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 99, background: "#E5787A" }} />
      <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 99, background: "#E0B24A" }} />
      <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 99, background: "#63B87A" }} />
      <span style={{ marginLeft: 8, color: SUB, fontSize: "0.8rem", fontFamily: SANS, fontWeight: 600 }}>
        your computer — macOS, Linux or Windows
      </span>
    </div>
    <div style={{ padding: "1.1rem 1.2rem", fontSize: "0.92rem", lineHeight: 1.85, color: INK }}>
      <div>
        <span style={{ color: VIOLET, fontWeight: 700 }}>$ </span>
        npx agentpager
      </div>
      <div style={{ color: SUB }}>&nbsp;&nbsp;scan the QR code below in the AgentPager app</div>
      <div style={{ color: SUB }}>&nbsp;&nbsp;paired · listening for sessions</div>
      <div style={{ marginTop: "0.6rem" }}>
        <span style={{ color: VIOLET, fontWeight: 700 }}>$ </span>
        agentpager ask <span style={{ color: PLUM }}>&quot;Deploy to production?&quot;</span> --timeout 5m
      </div>
      <div style={{ color: "#166534", fontWeight: 600 }}>&nbsp;&nbsp;approved from phone — exit 0</div>
    </div>
  </div>
);

const CHIPS = [
  "Claude Code",
  "Codex",
  "Gemini CLI",
  "OpenCode",
  "Qwen Code",
  "Goose",
  "One-tap approvals",
  "Hands-free voice",
  "Works on any network",
  "No account",
];

const FEATURES: { src: string; label: string; sub: string; alt: string }[] = [
  {
    src: SHOT + "/03.webp",
    alt: "AgentPager approval card showing the full shell command an agent wants to run, with Approve and Deny buttons",
    label: "The full command, then one tap",
    sub:
      "Every approval shows you exactly what the agent wants to run before you decide. Approve or deny in one tap. Destructive actions — push, delete, deploy — need a press-and-hold, and are never approved by voice.",
  },
  {
    src: SHOT + "/02.webp",
    alt: "AgentPager session screen streaming live output from a coding agent running on a computer",
    label: "Watch the session, not a summary",
    sub:
      "Open any session and read the output as it streams. Send the next prompt from your phone. If the computer is asleep, your prompt is held and delivered the moment it wakes.",
  },
  {
    src: SHOT + "/05.webp",
    alt: "AgentPager machine sheet listing paired computers, their agents and the projects found on each",
    label: "Every computer, every agent, one list",
    sub:
      "Continue the Claude Code and Codex sessions you started at your desk, or start a new one with Gemini CLI, OpenCode, Qwen Code or Goose in any project on that machine. It lists the repositories it finds, and you can browse for others.",
  },
  {
    src: SHOT + "/04.webp",
    alt: "AgentPager hands-free voice mode with a tap-to-talk button and a spoken summary of the agent's progress",
    label: "Hands-free while you walk away",
    sub:
      "Tap to talk, hear spoken summaries, approve by voice. Recognition is English; the app itself is in 15 languages. Destructive actions still need your thumb.",
  },
];

const NETWORK = [
  {
    h: "No same-WiFi rule",
    p: "Your phone and your computer both connect outbound to an encrypted relay. Mobile data, café WiFi, another country — it works the same.",
  },
  {
    h: "Nothing to open up",
    p: "No port forwarding, no inbound connection to your machine, no tunnel to configure, no account to create.",
  },
  {
    h: "It stays awake to finish",
    p: "While an agent is working, your computer is kept from idle-sleeping — so walking away does not freeze the run.",
  },
];

const TOOLKIT = [
  "Markdown editor with live preview",
  "Mermaid diagrams",
  "CLAUDE.md / AGENTS.md builder",
  "Prompt vault",
  "LAN vault server",
];

const FREE = [
  "One computer",
  "One live session at a time",
  "A daily allowance of live output",
  "Ads",
];
const PRO = [
  "Unlimited computers and sessions",
  "Hands-free voice mode",
  "Approval rules",
  "All-day live output",
  "No ads",
];

// These FAQ items are rendered visibly below AND serialised into FAQPage JSON-LD.
// They must stay identical — mismatched FAQ schema is a manual-action risk.
const FAQ: { q: string; a: string }[] = [
  {
    q: "Does it work when I'm not on my home network?",
    a: "Yes. Your phone and your computer each make an outbound connection to an encrypted relay, so they never need to be on the same WiFi. There is no port forwarding, no tunnel to configure and no inbound connection to your machine. Mobile data works exactly like home broadband.",
  },
  {
    q: "Do I need an account?",
    a: "No. There is no sign-up and no login. You pair your computer by scanning a QR code once, and that pairing is the only identity involved.",
  },
  {
    q: "Which coding agents does it work with?",
    a: "You can continue the Claude Code and Codex sessions you already started on your computer. You can also start new sessions with Gemini CLI, OpenCode (including DeepSeek), Qwen Code or Goose over the Agent Client Protocol, in any project on that computer.",
  },
  {
    q: "What happens if my computer goes to sleep?",
    a: "While an agent is working, AgentPager keeps your computer from idle-sleeping, so walking away does not freeze the run. If the computer is already asleep when you send a prompt, the prompt is held and delivered as soon as it wakes.",
  },
  {
    q: "Is my code sent anywhere?",
    a: "Your code never leaves your computer. The agent runs there, reads your files there, and only the session output you are looking at travels over the encrypted relay. One honest exception: when you browse for a project, your phone receives folder names so it can show you the list — never the contents of those folders.",
  },
  {
    q: "Can a script ask me for approval?",
    a: "Yes. Any script can page you: agentpager notify \"AAB built\" sends a notification, and agentpager ask \"Deploy to production?\" pauses the script until you answer — exit 0 if you approve, 1 if you deny, 2 if nobody answered. Add --timeout 5m and, when it expires, the request is withdrawn from your phone so it cannot be approved late.",
  },
  {
    q: "Is it free?",
    a: "Yes. The free tier gives you one computer, one live session at a time, a daily allowance of live output, and ads. Pro removes the limits — unlimited computers and sessions, hands-free voice, approval rules, all-day live output and no ads — as either a monthly subscription or a one-time lifetime purchase. Prices vary by country, so check the app.",
  },
  {
    q: "I had MDPilot. What happened to it?",
    a: "It is the same app, renamed. AgentPager is MDPilot with the agent features added — your documents and your Pro purchase carry over untouched, and the Toolkit (markdown editor, Mermaid diagrams, prompt vault) is all still there.",
  },
];

const SectionHead: React.FC<{ title: string; sub?: string }> = ({ title, sub }) => (
  <>
    <h2
      style={{
        fontWeight: 800,
        fontSize: "clamp(1.9rem,4.6vw,2.9rem)",
        letterSpacing: "-1.4px",
        textAlign: "center",
        margin: 0,
        color: INK,
        lineHeight: 1.1,
      }}
    >
      {title}
    </h2>
    {sub && (
      <p
        style={{
          fontSize: "1.14rem",
          color: SUB,
          textAlign: "center",
          margin: "0.85rem auto 2.6rem",
          maxWidth: 620,
          lineHeight: 1.55,
        }}
      >
        {sub}
      </p>
    )}
  </>
);

const AgentPagerLanding: React.FC = () => {
  const [showBar, setShowBar] = useState(false);
  const [open, setOpen] = useState<number | null>(0);

  useEffect(() => {
    const onScroll = () => setShowBar(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const softwareLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "AgentPager",
    operatingSystem: "Android",
    applicationCategory: "DeveloperApplication",
    url: PAGE,
    downloadUrl: PLAY,
    image: SITE + LOGO,
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
      description:
        "Free to download. Optional Pro upgrade via in-app purchase (monthly subscription or one-time lifetime), priced per country.",
    },
    description:
      "Continue the Claude Code and Codex sessions you started on your computer, from your phone. One approval queue across every machine and agent, the full command shown before you decide, one-tap approve or deny, and notifications when an agent needs you, finishes or fails. Works on any network over an encrypted relay, with no account.",
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Apps", item: SITE + "/apps" },
      { "@type": "ListItem", position: 2, name: "AgentPager", item: PAGE },
    ],
  };

  return (
    <>
      <Helmet>
        <title>AgentPager — Approve Claude Code &amp; Codex From Your Phone</title>
        <meta
          name="description"
          content="Continue your Claude Code and Codex sessions from your phone. See the full command, approve or deny in one tap. Any network, no account, code stays put."
        />
        <link rel="canonical" href={PAGE} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta
          name="keywords"
          content="claude code from phone, claude code mobile app, codex mobile, approve claude code remotely, run coding agent from phone, remote coding agent android, agent client protocol app, gemini cli phone, opencode mobile, goose agent android"
        />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cosmovex Apps" />
        <meta property="og:title" content="AgentPager — Approve Claude Code &amp; Codex From Your Phone" />
        <meta
          property="og:description"
          content="Your agent stopped and is waiting for a yes. Continue Claude Code and Codex sessions from your phone, see the full command, approve in one tap. Any network, no account."
        />
        <meta property="og:url" content={PAGE} />
        <meta property="og:image" content={SITE + BANNER} />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="500" />
        <meta property="og:image:alt" content="AgentPager — approve your coding agent from your phone" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AgentPager — Approve Claude Code &amp; Codex From Your Phone" />
        <meta
          name="twitter:description"
          content="Continue Claude Code and Codex sessions from your phone. Full command shown, one-tap approve. Any network, no account, your code stays on your computer."
        />
        <meta name="twitter:image" content={SITE + BANNER} />
        <script type="application/ld+json">{JSON.stringify(softwareLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <div style={{ background: BG, minHeight: "100vh", fontFamily: SANS, color: INK }}>
        {/* NAV */}
        <nav
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "1.25rem 1.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
          }}
        >
          <a
            href={PAGE}
            style={{ display: "flex", alignItems: "center", gap: "0.7rem", textDecoration: "none", color: INK }}
          >
            <img src={LOGO} alt="AgentPager app icon" width={44} height={44} style={{ borderRadius: 12 }} />
            <span style={{ fontWeight: 700, fontSize: "1.12rem", letterSpacing: "-0.4px" }}>AgentPager</span>
          </a>
          <PlayButton small />
        </nav>

        {/* HERO */}
        <header
          className="ap-hero"
          style={{
            maxWidth: 1180,
            margin: "0 auto",
            padding: "2rem 1.5rem 1rem",
            display: "grid",
            gridTemplateColumns: "1.05fr 0.95fr",
            gap: "2.6rem",
            alignItems: "center",
          }}
        >
          <div>
            <p
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.45rem",
                background: TINT,
                border: `1px solid ${LINE}`,
                color: PLUM,
                fontWeight: 700,
                fontSize: "0.88rem",
                padding: "0.42rem 0.85rem",
                borderRadius: 999,
                margin: "0 0 1.2rem",
              }}
            >
              <span aria-hidden="true">📣</span> Free on Android · formerly MDPilot
            </p>
            <h1
              style={{
                fontWeight: 800,
                fontSize: "clamp(2.35rem,5.6vw,4.05rem)",
                lineHeight: 1.03,
                letterSpacing: "-2.2px",
                margin: 0,
                color: INK,
              }}
            >
              Your agent stopped.
              <br />
              <span
                style={{
                  background: `linear-gradient(90deg, ${PLUM}, ${VIOLET})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Say yes from your phone.
              </span>
            </h1>
            <p style={{ fontSize: "1.28rem", color: BODY, lineHeight: 1.45, margin: "1.3rem 0 1.9rem", maxWidth: 520 }}>
              Continue the Claude Code and Codex sessions you started at your desk — see the full command it is waiting
              on, and approve or deny in one tap.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.8rem", alignItems: "center" }}>
              <PlayButton />
              <GetButton />
            </div>
            <PrivacyNote />
          </div>

          {/* Real screenshot above the fold: the "Needs you" approval queue */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Phone
              src={SHOT + "/01.webp"}
              alt="The AgentPager Needs you queue on a phone, listing coding agents waiting for approval across several computers"
              w={272}
              eager
            />
          </div>
        </header>

        {/* CHIP STRIP */}
        <div
          style={{
            maxWidth: 1000,
            margin: "1.8rem auto 0",
            padding: "0 1.5rem",
            display: "flex",
            gap: "0.6rem",
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {CHIPS.map((t) => (
            <span
              key={t}
              style={{
                background: CARD,
                border: `1px solid ${LINE}`,
                color: SUB,
                fontWeight: 600,
                fontSize: "0.88rem",
                padding: "0.45rem 0.95rem",
                borderRadius: 999,
              }}
            >
              {t}
            </span>
          ))}
        </div>

        {/* THE QUEUE */}
        <section style={{ maxWidth: 1000, margin: "0 auto", padding: "3.6rem 1.5rem 0" }}>
          <SectionHead
            title="One queue. Every computer, every agent."
            sub="Approvals first, then problems, then finished work — so the thing that is actually blocking you is always at the top."
          />
          <ol
            className="ap-queue"
            style={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1rem",
            }}
          >
            {[
              { n: "1", h: "Needs you", p: "An agent has stopped and is waiting on a yes or a no.", c: "#B42318", bgc: "#FEF3F2" },
              { n: "2", h: "Went wrong", p: "A run failed, and you can read why without unlocking a laptop.", c: PLUM, bgc: TINT },
              { n: "3", h: "Done", p: "Finished work, so you know what landed while you were out.", c: "#166534", bgc: "#ECFDF5" },
            ].map((q) => (
              <li
                key={q.h}
                style={{
                  background: CARD,
                  border: `1px solid ${LINE}`,
                  borderRadius: 18,
                  padding: "1.5rem 1.4rem",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: q.bgc,
                    color: q.c,
                    fontWeight: 800,
                    fontFamily: MONO,
                    marginBottom: "0.9rem",
                  }}
                  aria-hidden="true"
                >
                  {q.n}
                </span>
                <h3 style={{ margin: "0 0 0.4rem", fontSize: "1.18rem", fontWeight: 800, color: INK }}>{q.h}</h3>
                <p style={{ margin: 0, color: SUB, fontSize: "1.02rem", lineHeight: 1.55 }}>{q.p}</p>
              </li>
            ))}
          </ol>
          <p style={{ textAlign: "center", color: MUTED, fontSize: "0.98rem", margin: "1.6rem 0 0" }}>
            Notifications tell you the moment an agent needs you, finishes, or fails.
          </p>
        </section>

        {/* FEATURE BANNER */}
        <section style={{ maxWidth: 1060, margin: "0 auto", padding: "3rem 1.5rem 0" }}>
          <figure style={{ margin: 0 }}>
            <img
              src={BANNER}
              alt="AgentPager feature banner: a coding agent on a computer paired with an approval queue on a phone"
              width={1024}
              height={500}
              loading="lazy"
              decoding="async"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                borderRadius: 22,
                border: `1px solid ${LINE}`,
                boxShadow: "0 24px 50px -30px rgba(42,19,88,0.35)",
              }}
            />
          </figure>
        </section>

        {/* SETUP */}
        <section style={{ maxWidth: 1120, margin: "0 auto", padding: "3.8rem 1.5rem 0" }}>
          <SectionHead
            title="Two steps, and you are paired"
            sub="One command on the computer, one QR scan on the phone. Node 20 or newer, on macOS, Linux or Windows."
          />
          <div
            className="ap-setup"
            style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: "2.4rem", alignItems: "center" }}
          >
            <div>
              <Terminal />
              <ol style={{ margin: "1.6rem 0 0", paddingLeft: "1.2rem", color: BODY, fontSize: "1.08rem", lineHeight: 1.7 }}>
                <li>
                  Run <code style={{ fontFamily: MONO, color: PLUM, fontWeight: 700 }}>npx agentpager</code> in a terminal
                  on the computer your agents run on.
                </li>
                <li>Open the app and scan the QR code it prints. That is the whole setup.</li>
              </ol>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Phone
                src={SHOT + "/06.webp"}
                alt="AgentPager pairing screen scanning the QR code printed by the npx agentpager command"
                w={224}
              />
            </div>
          </div>
        </section>

        {/* FEATURES — alternating screenshot rows */}
        <section style={{ maxWidth: 1120, margin: "0 auto", padding: "4.2rem 1.5rem 0" }}>
          <SectionHead
            title="What you can actually do from the bus"
            sub="Not a dashboard of green ticks — the real session, the real command, and the decision that unblocks it."
          />
          {FEATURES.map((f, i) => (
            <div
              key={f.label}
              className="ap-row"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "2.5rem",
                alignItems: "center",
                margin: "0 0 3.2rem",
              }}
            >
              <div
                style={{
                  order: i % 2 === 0 ? 0 : 2,
                  display: "flex",
                  justifyContent: "center",
                  background: i % 2 === 0 ? TINT : FAINT,
                  border: `1px solid ${LINE}`,
                  borderRadius: 26,
                  padding: "2.2rem 1rem 0",
                  overflow: "hidden",
                  maxHeight: 440,
                }}
              >
                <Phone src={f.src} alt={f.alt} w={214} />
              </div>
              <div style={{ order: 1 }}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: TINT,
                    color: PLUM,
                    fontWeight: 800,
                    fontFamily: MONO,
                    marginBottom: "1rem",
                  }}
                  aria-hidden="true"
                >
                  {i + 1}
                </span>
                <h3
                  style={{
                    fontWeight: 800,
                    fontSize: "clamp(1.55rem,3.2vw,2.15rem)",
                    letterSpacing: "-1px",
                    margin: 0,
                    color: INK,
                    lineHeight: 1.12,
                  }}
                >
                  {f.label}
                </h3>
                <p style={{ fontSize: "1.12rem", color: BODY, lineHeight: 1.6, margin: "0.8rem 0 0", maxWidth: 450 }}>
                  {f.sub}
                </p>
              </div>
            </div>
          ))}
        </section>

        {/* SCRIPTS CAN PAGE YOU */}
        <section
          style={{
            background: CARD,
            borderTop: `1px solid ${LINE}`,
            borderBottom: `1px solid ${LINE}`,
            padding: "4rem 1.5rem",
          }}
        >
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <SectionHead
              title="Any script can page you"
              sub="Two commands turn a build, a cron job or a deploy step into something that waits for your thumb."
            />
            <div
              className="ap-cli"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.6rem", alignItems: "stretch" }}
            >
              <div style={{ background: FAINT, border: `1px solid ${LINE}`, borderRadius: 18, padding: "1.5rem 1.5rem" }}>
                <h3 style={{ margin: "0 0 0.7rem", fontSize: "1.12rem", fontWeight: 800, color: INK }}>
                  Tell me when it is done
                </h3>
                <pre
                  style={{
                    margin: 0,
                    fontFamily: MONO,
                    fontSize: "0.92rem",
                    color: INK,
                    background: CARD,
                    border: `1px solid ${LINE}`,
                    borderRadius: 12,
                    padding: "0.9rem 1rem",
                    overflowX: "auto",
                    lineHeight: 1.6,
                  }}
                >
                  <code>
                    <span style={{ color: VIOLET, fontWeight: 700 }}>$ </span>
                    agentpager notify <span style={{ color: PLUM }}>&quot;AAB built&quot;</span>
                  </code>
                </pre>
                <p style={{ margin: "0.9rem 0 0", color: SUB, fontSize: "1rem", lineHeight: 1.6 }}>
                  A notification lands on your phone. The script carries on.
                </p>
              </div>

              <div style={{ background: FAINT, border: `1px solid ${LINE}`, borderRadius: 18, padding: "1.5rem 1.5rem" }}>
                <h3 style={{ margin: "0 0 0.7rem", fontSize: "1.12rem", fontWeight: 800, color: INK }}>
                  Ask me before you do it
                </h3>
                <pre
                  style={{
                    margin: 0,
                    fontFamily: MONO,
                    fontSize: "0.92rem",
                    color: INK,
                    background: CARD,
                    border: `1px solid ${LINE}`,
                    borderRadius: 12,
                    padding: "0.9rem 1rem",
                    overflowX: "auto",
                    lineHeight: 1.6,
                  }}
                >
                  <code>
                    <span style={{ color: VIOLET, fontWeight: 700 }}>$ </span>
                    agentpager ask <span style={{ color: PLUM }}>&quot;Deploy to production?&quot;</span>
                    {"\n"}
                    <span style={{ color: MUTED }}>{"      "}--timeout 5m</span>
                  </code>
                </pre>
                <p style={{ margin: "0.9rem 0 0", color: SUB, fontSize: "1rem", lineHeight: 1.6 }}>
                  The script pauses and continues only if you approve.{" "}
                  <strong style={{ color: INK }}>Exit 0</strong> approve,{" "}
                  <strong style={{ color: INK }}>1</strong> deny, <strong style={{ color: INK }}>2</strong> nobody
                  answered.
                </p>
              </div>
            </div>
            <p style={{ textAlign: "center", color: MUTED, fontSize: "0.98rem", margin: "1.6rem auto 0", maxWidth: 640, lineHeight: 1.6 }}>
              When a timeout expires, the request is withdrawn from your phone — so a question you never saw cannot be
              approved an hour late.
            </p>
          </div>
        </section>

        {/* NETWORK / PRIVACY */}
        <section style={{ maxWidth: 1060, margin: "0 auto", padding: "4rem 1.5rem 0" }}>
          <SectionHead
            title="It works from wherever you are"
            sub="No same-WiFi requirement, no router settings, no account. Both ends dial out to an encrypted relay and meet in the middle."
          />
          <div
            className="ap-net"
            style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem" }}
          >
            {NETWORK.map((n) => (
              <div
                key={n.h}
                style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 18, padding: "1.5rem 1.4rem" }}
              >
                <h3 style={{ margin: "0 0 0.45rem", fontSize: "1.14rem", fontWeight: 800, color: INK }}>{n.h}</h3>
                <p style={{ margin: 0, color: SUB, fontSize: "1.02rem", lineHeight: 1.6 }}>{n.p}</p>
              </div>
            ))}
          </div>
          <div
            style={{
              maxWidth: 760,
              margin: "1.8rem auto 0",
              background: TINT,
              border: `1px solid ${LINE}`,
              borderRadius: 18,
              padding: "1.5rem 1.6rem",
            }}
          >
            <h3 style={{ margin: "0 0 0.5rem", fontSize: "1.1rem", fontWeight: 800, color: PLUM }}>
              🔒 Your code never leaves your computer
            </h3>
            <p style={{ margin: 0, color: SUB, fontSize: "1.02rem", lineHeight: 1.65 }}>
              The agent runs on your machine and reads your files there. Only the session output you are looking at
              travels, end-to-end encrypted, and there is no account behind any of it. The one thing your phone does
              receive is folder <em>names</em>, when you browse that computer for a project to open — never the contents
              of those folders.
            </p>
          </div>
        </section>

        {/* TOOLKIT */}
        <section style={{ maxWidth: 1120, margin: "0 auto", padding: "4.2rem 1.5rem 0" }}>
          <div
            className="ap-toolkit"
            style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: "2.4rem", alignItems: "center" }}
          >
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Phone
                src={SHOT + "/07.webp"}
                alt="The AgentPager Toolkit markdown editor with a live preview of the rendered document"
                w={224}
              />
            </div>
            <div>
              <h2
                style={{
                  fontWeight: 800,
                  fontSize: "clamp(1.9rem,4.2vw,2.7rem)",
                  letterSpacing: "-1.3px",
                  margin: 0,
                  color: INK,
                  lineHeight: 1.1,
                }}
              >
                The Toolkit comes with it
              </h2>
              <p style={{ fontSize: "1.14rem", color: BODY, lineHeight: 1.6, margin: "0.85rem 0 1.4rem", maxWidth: 460 }}>
                Everything MDPilot did is still here — the editor you write your agent instructions in, on the same
                phone you approve them from.
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.6rem" }}>
                {TOOLKIT.map((t) => (
                  <li key={t} style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start", color: BODY, fontSize: "1.06rem" }}>
                    <span aria-hidden="true" style={{ color: VIOLET, fontWeight: 800 }}>
                      ✓
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FREE / PRO */}
        <section style={{ maxWidth: 900, margin: "0 auto", padding: "4.2rem 1.5rem 0" }}>
          <SectionHead
            title="Free to use, Pro when you live in it"
            sub="Prices differ by country, so the app shows you yours. Pro is either a monthly subscription or a one-time lifetime purchase — your choice."
          />
          <div className="ap-plans" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem" }}>
            <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 20, padding: "1.8rem 1.7rem" }}>
              <h3 style={{ margin: "0 0 0.3rem", fontSize: "1.3rem", fontWeight: 800, color: INK }}>Free</h3>
              <p style={{ margin: "0 0 1.1rem", color: MUTED, fontSize: "0.98rem" }}>Enough to run your day from a phone.</p>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                {FREE.map((t) => (
                  <li key={t} style={{ display: "flex", gap: "0.6rem", color: SUB, fontSize: "1.04rem", lineHeight: 1.5 }}>
                    <span aria-hidden="true" style={{ color: MUTED }}>
                      •
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
            <div
              style={{
                background: TINT,
                border: `1.5px solid ${VIOLET}`,
                borderRadius: 20,
                padding: "1.8rem 1.7rem",
              }}
            >
              <h3 style={{ margin: "0 0 0.3rem", fontSize: "1.3rem", fontWeight: 800, color: PLUM }}>Pro</h3>
              <p style={{ margin: "0 0 1.1rem", color: SUB, fontSize: "0.98rem" }}>
                Monthly subscription or one-time lifetime.
              </p>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                {PRO.map((t) => (
                  <li key={t} style={{ display: "flex", gap: "0.6rem", color: SUB, fontSize: "1.04rem", lineHeight: 1.5 }}>
                    <span aria-hidden="true" style={{ color: VIOLET, fontWeight: 800 }}>
                      ✓
                    </span>
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ maxWidth: 820, margin: "0 auto", padding: "4.2rem 1.5rem 0" }}>
          <SectionHead title="Questions, answered" />
          <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem", marginTop: "-1.4rem" }}>
            {FAQ.map((f, i) => (
              <div
                key={f.q}
                style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 16, overflow: "hidden" }}
              >
                <h3 style={{ margin: 0 }}>
                  <button
                    type="button"
                    onClick={() => setOpen(open === i ? null : i)}
                    aria-expanded={open === i}
                    aria-controls={"ap-faq-" + i}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "1rem",
                      padding: "1.1rem 1.35rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      fontFamily: SANS,
                      fontWeight: 700,
                      fontSize: "1.06rem",
                      lineHeight: 1.4,
                      color: INK,
                    }}
                  >
                    {f.q}
                    <span
                      aria-hidden="true"
                      style={{
                        color: VIOLET,
                        fontSize: "1.5rem",
                        lineHeight: 1,
                        flexShrink: 0,
                        transform: open === i ? "rotate(45deg)" : "none",
                        transition: "transform .2s",
                      }}
                    >
                      +
                    </span>
                  </button>
                </h3>
                {/* Always rendered so the answer text is present in the HTML and
                    matches the FAQPage schema exactly; collapsed with CSS only. */}
                <p
                  id={"ap-faq-" + i}
                  style={{
                    display: open === i ? "block" : "none",
                    margin: 0,
                    padding: "0 1.35rem 1.25rem",
                    color: BODY,
                    fontSize: "1.02rem",
                    lineHeight: 1.65,
                  }}
                >
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FINAL CTA */}
        <section style={{ padding: "4rem 1.5rem 3rem" }}>
          <div
            style={{
              maxWidth: 900,
              margin: "0 auto",
              background: `linear-gradient(135deg, ${DEEP} 0%, ${PLUM} 100%)`,
              borderRadius: 28,
              padding: "3.2rem 2rem",
              textAlign: "center",
              color: "#FFFFFF",
              boxShadow: "0 30px 60px -28px rgba(42,19,88,0.55)",
            }}
          >
            <h2 style={{ fontWeight: 800, fontSize: "clamp(1.9rem,4.6vw,2.8rem)", letterSpacing: "-1.4px", margin: 0 }}>
              Stop being the bottleneck.
            </h2>
            <p style={{ fontSize: "1.16rem", color: "#DCD2F5", lineHeight: 1.55, margin: "1rem auto 2rem", maxWidth: 540 }}>
              Pair your computer once, and the next time an agent needs a yes, it asks the phone in your pocket.
            </p>
            <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: "0.8rem" }}>
              <PlayButton />
              <GetButton onDark />
            </div>
            <PrivacyNote center onDark />
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ maxWidth: 1060, margin: "0 auto", padding: "0 1.5rem 7rem" }}>
          <div
            style={{
              borderTop: `1px solid ${LINE}`,
              paddingTop: "1.6rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "1rem",
              color: MUTED,
              fontSize: "0.95rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
              <img src={LOGO} alt="" width={32} height={32} loading="lazy" style={{ borderRadius: 9 }} />
              <span>© Cosmovex · AgentPager</span>
            </div>
            <nav style={{ display: "flex", gap: "1.3rem", flexWrap: "wrap" }}>
              <a href="/apps/agentpager/privacy-policy" style={{ color: SUB, textDecoration: "none" }}>
                Privacy policy
              </a>
              <a href="/apps/agentpager/support" style={{ color: SUB, textDecoration: "none" }}>
                Support
              </a>
              <a href={GET} style={{ color: PLUM, fontWeight: 700, textDecoration: "none" }}>
                Get the app
              </a>
            </nav>
          </div>
          <p style={{ color: MUTED, fontSize: "0.86rem", lineHeight: 1.6, margin: "1.3rem 0 0", maxWidth: 780 }}>
            AgentPager is an independent app from Cosmovex. It is not affiliated with, endorsed by or sponsored by
            Anthropic, OpenAI or Google. Claude and Claude Code are trademarks of Anthropic; Codex is a trademark of
            OpenAI; Gemini is a trademark of Google. All other names are the property of their respective owners.
          </p>
        </footer>
      </div>

      {/* STICKY INSTALL BAR */}
      {showBar && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "0.65rem 1rem calc(0.65rem + env(safe-area-inset-bottom))",
            background: "rgba(250,248,255,0.97)",
            backdropFilter: "blur(14px)",
            borderTop: `1px solid ${LINE}`,
          }}
        >
          <img src={LOGO} alt="" width={40} height={40} loading="lazy" style={{ borderRadius: 10 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "0.95rem", color: INK }}>AgentPager</div>
            <div style={{ color: SUB, fontSize: "0.78rem" }}>Free on Google Play · no account</div>
          </div>
          <PlayButton small />
        </div>
      )}

      <style>{`
        @media (max-width: 900px){
          .ap-hero{ grid-template-columns: 1fr !important; text-align:center; padding-top:1rem !important; }
          .ap-hero > div:first-child p{ margin-left:auto; margin-right:auto; }
          .ap-hero > div:first-child > div{ justify-content:center; }
          .ap-setup, .ap-toolkit, .ap-cli{ grid-template-columns: 1fr !important; gap:1.6rem !important; }
          .ap-toolkit > div:first-child{ order:2; }
          .ap-queue, .ap-net{ grid-template-columns: 1fr !important; }
          .ap-row{ grid-template-columns: 1fr !important; gap:1.5rem !important; text-align:center; }
          .ap-row > div:first-child{ order:0 !important; }
          .ap-row > div:last-child{ order:1 !important; }
          .ap-row > div:last-child > span:first-child{ margin-left:auto; margin-right:auto; }
          .ap-row p{ margin-left:auto; margin-right:auto; }
          .ap-setup ol{ text-align:left; }
          .ap-setup > div:first-child > div{ margin-left:auto; margin-right:auto; }
        }
        @media (max-width: 560px){
          .ap-plans{ grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
};

export default AgentPagerLanding;
